import { useEffect, useState } from 'react';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';
import { NETWORK_CONFIG } from '@/config/networks';
import { showToast } from '@/utils/toast';

export const useNetworkValidation = () => {
  const { address, isConnected } = useAccount();
  const chainId = useChainId();
  const { switchChain } = useSwitchChain();
  const [isWrongNetwork, setIsWrongNetwork] = useState(false);
  const [hasShownToast, setHasShownToast] = useState(false);

  const expectedChainId = NETWORK_CONFIG.chainId;
  const networkName = NETWORK_CONFIG.networkName;

  useEffect(() => {
    if (isConnected && address && chainId) {
      const wrongNetwork = chainId !== expectedChainId;
      setIsWrongNetwork(wrongNetwork);

      if (wrongNetwork && !hasShownToast) {
        showToast.error(
          `Please switch to ${networkName} (Chain ID: ${expectedChainId})`,
          {
            style: {
              background: '#f44336',
              color: '#fff',
            },
            duration: 5000,
          }
        );
        setHasShownToast(true);
      } else if (!wrongNetwork && hasShownToast) {
        setHasShownToast(false);
      }
    } else {
      setIsWrongNetwork(false);
      setHasShownToast(false);
    }
  }, [
    isConnected,
    address,
    chainId,
    expectedChainId,
    networkName,
    hasShownToast,
  ]);

  const handleSwitchNetwork = async () => {
    if (!switchChain) {
      showToast.error('Network switching not supported by wallet', {
        style: {
          background: '#f44336',
          color: '#fff',
        },
      });
      return;
    }

    try {
      await switchChain({ chainId: expectedChainId });
      // Force close modal immediately after successful switch
      setIsWrongNetwork(false);
      showToast.success(`Successfully switched to ${networkName}`, {
        style: {
          background: '#4CAF50',
          color: '#fff',
        },
      });
    } catch (error) {
      console.error('Failed to switch network:', error);

      // If switch failed, try to add the network first
      if (
        (error as any)?.message?.includes('Unrecognized chain ID') ||
        (error as any)?.code === 4902
      ) {
        try {
          // Add Sei network if not exists
          const ethereum = (window as any).ethereum;
          if (ethereum) {
            await ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [
                {
                  chainId: `0x${expectedChainId.toString(16)}`,
                  chainName: networkName,
                  rpcUrls: [NETWORK_CONFIG.rpcUrl],
                  nativeCurrency: {
                    name: 'SEI',
                    symbol: 'SEI',
                    decimals: 18,
                  },
                  blockExplorerUrls: [NETWORK_CONFIG.blockExplorerUrl],
                },
              ],
            });
            showToast.success(
              `Added ${networkName} to wallet. Please try switching again.`,
              {
                style: {
                  background: '#4CAF50',
                  color: '#fff',
                },
              }
            );
          } else {
            showToast.error(
              'Failed to switch network. Please add the network manually in your wallet.',
              {
                style: {
                  background: '#f44336',
                  color: '#fff',
                },
                duration: 5000,
              }
            );
          }
        } catch (addError) {
          console.error('Failed to add network:', addError);
          showToast.error(
            'Failed to add network. Please add manually in your wallet.',
            {
              style: {
                background: '#f44336',
                color: '#fff',
              },
              duration: 5000,
            }
          );
        }
      } else {
        showToast.error(
          'Failed to switch network. Please switch manually in your wallet.',
          {
            style: {
              background: '#f44336',
              color: '#fff',
            },
            duration: 5000,
          }
        );
      }
    }
  };

  return {
    isWrongNetwork,
    currentChainId: chainId,
    expectedChainId,
    networkName,
    switchNetwork: handleSwitchNetwork,
    canSwitchNetwork: !!switchChain,
  };
};
